import type { ProjectField } from '../types';

export const transformJiraFormValues = async (
  formValues: Record<string, any>,
  selectedIssueTypeFields: ProjectField[],
): Promise<Record<string, any>> => {
  const transformedValues: Record<string, any> = {};

  for (const [key, value] of Object.entries(formValues)) {
    if (!value) {
      continue;
    }

    if (key === 'issueType') {
      transformedValues[key] = value;
      continue;
    }

    const field = selectedIssueTypeFields.find((f) => f.key === key);

    if (!field) {
      transformedValues[key] = value;
      continue;
    }

    if (
      field.schema_info.type === 'array' &&
      field.schema_info.items === 'attachment'
    ) {
      const attachmentValue = await transformAttachmentField(value);
      if (attachmentValue) {
        transformedValues[key] = attachmentValue;
      }
      continue;
    }

    const isUserField =
      field.schema_info.type === 'user' ||
      (field.schema_info.type === 'array' &&
        field.schema_info.items === 'user') ||
      key === 'reporter' ||
      key === 'assignee';

    if (isUserField) {
      transformedValues[key] = transformUserField(value, field);
      continue;
    }

    if (field.schema_info.type === 'array') {
      transformedValues[key] = transformArrayField(value, field);
      continue;
    }

    if (field.schema_info.type === 'number') {
      const numValue = Number(value);
      if (!isNaN(numValue)) {
        transformedValues[key] = numValue;
      }
      continue;
    }

    if (
      field.allowed_values &&
      field.allowed_values.length > 0 &&
      typeof value === 'string'
    ) {
      transformedValues[key] = { id: value };
      continue;
    }

    // Autocomplete fields return option objects {value, label}.
    // Teams fields need the plain UUID string; all other autocomplete fields use {id: ...}.
    if (
      value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      'value' in value
    ) {
      const isTeamsField = field.name?.toLowerCase() === 'team';
      transformedValues[key] = isTeamsField
        ? (value as any).value
        : { id: (value as any).value };
      continue;
    }

    transformedValues[key] = value;
  }

  return transformedValues;
};

const transformAttachmentField = async (
  value: File | File[],
): Promise<[string, string][] | null> => {
  if (value instanceof File) {
    const arrayBuffer = await value.arrayBuffer();
    const base64 = btoa(
      new Uint8Array(arrayBuffer).reduce(
        (data, byte) => data + String.fromCharCode(byte),
        '',
      ),
    );
    return [[value.name, base64]];
  }

  if (Array.isArray(value) && value.length > 0) {
    const attachmentPromises = value.map(async (file: File) => {
      const arrayBuffer = await file.arrayBuffer();
      const base64 = btoa(
        new Uint8Array(arrayBuffer).reduce(
          (data, byte) => data + String.fromCharCode(byte),
          '',
        ),
      );
      return [file.name, base64] as [string, string];
    });
    return await Promise.all(attachmentPromises);
  }

  return null;
};

const transformUserField = (value: any, field: ProjectField): any => {
  if (field.schema_info.type === 'array') {
    const arrayValue = Array.isArray(value) ? value : [value];
    return arrayValue.map((v) => ({
      accountId: typeof v === 'string' ? v : v.value,
    }));
  }

  const accountId = typeof value === 'string' ? value : value.value;
  return { accountId };
};

const transformArrayField = (value: any, field: ProjectField): any => {
  const arrayValue = Array.isArray(value) ? value : [value];

  if (field.schema_info.items === 'string') {
    return arrayValue.map((v) => (typeof v === 'string' ? v : v.value));
  }

  if (field.schema_info.items === 'number') {
    return arrayValue.map((v) => Number(v)).filter((v) => !isNaN(v));
  }

  if (field.schema_info.items === 'json') {
    // Sprint field: Jira expects a plain integer ID, not an array or wrapped object
    const first = arrayValue[0];
    const raw =
      typeof first === 'string'
        ? first
        : first && typeof first === 'object' && 'value' in first
        ? String((first as any).value)
        : String(first);
    const num = parseInt(raw, 10);
    return isNaN(num) ? raw : num;
  }

  if (field.schema_info.items && field.schema_info.items !== 'string') {
    return arrayValue.map((v) => {
      if (typeof v === 'string') return { id: v };
      if (v && typeof v === 'object' && 'value' in v)
        return { id: (v as any).value };
      return v;
    });
  }

  return arrayValue;
};
